import { type BaseDocumentPayload, type ChatMessagePayload, type ChunkItem } from '#app/services/ai/types.ts'
import {
	cgrDocumentAsTitle,
	costaRicaCgrCitation,
	getCgrDocumentUrl,
} from '#app/services/legal-ai/countries/costa-rica/sources/cgr/citation.ts'
import { costaRicaMtssCitation } from '#app/services/legal-ai/countries/costa-rica/sources/mtss/citation.ts'
import {
	cleanUrlOfLaw,
	costaRicaNormativaCitation,
	lawDocumentAsTitle,
} from '#app/services/legal-ai/countries/costa-rica/sources/normativa/citation.ts'
import {
	costaRicaResolucionesCitation,
	getNexusUrl,
	resolutionNumberAsTitle,
} from '#app/services/legal-ai/countries/costa-rica/sources/resoluciones/citation.ts'
import {
	costaRicaTraCitation,
	getTraDocumentUrl,
	traDocumentAsTitle,
} from '#app/services/legal-ai/countries/costa-rica/sources/tra/citation.ts'
import { colombiaSourceDefinitions } from '#app/services/legal-ai/countries/colombia/sources.ts'
import { panamaSourceDefinitions } from '#app/services/legal-ai/countries/panama/sources.ts'
import {
	type LegalSourceCitationBehavior,
	type LegalSourceCitationType,
	type LegalSourceLegacyPayloadKey,
} from '#app/services/legal-ai/shared/source-contract.ts'
import { findLegalSourcePayload, getLegalSourcePayloads } from '#app/services/legal-ai/shared/source-payloads.ts'
import { officialPublicationCitationDefinitions } from '#app/services/official-publications/citation-registry.ts'

export {
	cgrDocumentAsTitle,
	cleanUrlOfLaw,
	getCgrDocumentUrl,
	getNexusUrl,
	getTraDocumentUrl,
	lawDocumentAsTitle,
	resolutionNumberAsTitle,
	traDocumentAsTitle,
}

export type LegalSourceCitationDefinition<Payload extends BaseDocumentPayload = BaseDocumentPayload> = {
	id: string
	legacyPayloadKey?: LegalSourceLegacyPayloadKey
	citationType: LegalSourceCitationType
	citation: LegalSourceCitationBehavior<Payload>
}

export const legalSourceCitationDefinitions = [
	{
		id: 'cr/normativa',
		legacyPayloadKey: 'laws',
		citationType: 'Ley',
		citation: costaRicaNormativaCitation,
	},
	{
		id: 'cr/resoluciones',
		legacyPayloadKey: 'resolutions',
		citationType: 'Resolucion',
		citation: costaRicaResolucionesCitation,
	},
	{
		id: 'cr/cgr',
		legacyPayloadKey: 'cgr',
		citationType: 'Cgr',
		citation: costaRicaCgrCitation,
	},
	{
		id: 'cr/tra',
		legacyPayloadKey: 'tra',
		citationType: 'Tra',
		citation: costaRicaTraCitation,
	},
	{
		id: 'cr/mtss',
		citationType: 'Mtss',
		citation: costaRicaMtssCitation,
	},
	...panamaSourceDefinitions,
	...colombiaSourceDefinitions,
	...officialPublicationCitationDefinitions.map(source => ({
		id: source.id,
		citationType: source.citationType,
		citation: source.citation,
	})),
] as const satisfies readonly LegalSourceCitationDefinition<any>[]

export type LegalSourceCitationDefinitionAny = (typeof legalSourceCitationDefinitions)[number]
export type LegalPayloadWithSource = {
	source: LegalSourceCitationDefinitionAny
	payload: ChunkItem<BaseDocumentPayload>['payload']
}

export function getLegalSourceCitationByPayloadKey(payloadKey: LegalSourceLegacyPayloadKey) {
	return (
		legalSourceCitationDefinitions.find(
			source => 'legacyPayloadKey' in source && source.legacyPayloadKey === payloadKey,
		) ?? null
	)
}

export function getLegalSourceCitationByCitationType(citationType: LegalSourceCitationType) {
	return legalSourceCitationDefinitions.find(source => source.citationType === citationType) ?? null
}

export function getLegalSourceCitationById(sourceId: string) {
	return legalSourceCitationDefinitions.find(source => source.id === sourceId) ?? null
}

export function getLegalSourceCitationByInlineTagName(tagName: string) {
	const normalizedTagName = tagName.toLowerCase()
	return (
		legalSourceCitationDefinitions.find(source => {
			if (source.citation.inlineTagName.toLowerCase() === normalizedTagName) return true
			return source.citation.legacyTagNames?.some(name => name.toLowerCase() === normalizedTagName) ?? false
		}) ?? null
	)
}

export function getRegisteredLegalSourcePayloads(
	payloads: ChatMessagePayload,
	source: LegalSourceCitationDefinitionAny,
) {
	return getLegalSourcePayloads(payloads, source)
}

export function findRegisteredLegalSourcePayload(
	payloads: ChatMessagePayload,
	documentId: string,
	source: LegalSourceCitationDefinitionAny,
) {
	return findLegalSourcePayload(payloads, documentId, source)
}

export function findLegalPayloadByDocumentId(payloads: ChatMessagePayload, documentId: string) {
	let result: LegalPayloadWithSource | null = null
	for (const source of legalSourceCitationDefinitions) {
		const payload = findRegisteredLegalSourcePayload(payloads, documentId, source)
		if (!payload) continue
		if (result) return null
		result = { source, payload }
	}
	return result
}

export function buildLegalPayloadByDocumentId(payloads: ChatMessagePayload) {
	const result = new Map<string, LegalPayloadWithSource>()
	const ambiguousIds = new Set<string>()
	for (const source of legalSourceCitationDefinitions) {
		for (const payload of getRegisteredLegalSourcePayloads(payloads, source)) {
			const documentId = String(payload.idDocument ?? '').trim()
			if (!documentId || ambiguousIds.has(documentId)) continue
			const existing = result.get(documentId)
			// An id-only lookup cannot choose between distinct source namespaces.
			if (existing && existing.source.id !== source.id) {
				result.delete(documentId)
				ambiguousIds.add(documentId)
			} else if (!existing) {
				result.set(documentId, { source, payload })
			}
		}
	}
	return result
}
